import { useEffect, useState } from "react";
import { settingsService } from "../../services/apiService";
import { showToast } from "../../utils/toast";
import { validateForm } from "../../utils/validate";
import { API_CONFIG } from "../../config/api";

export default function Settings() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState(null);

  useEffect(() => {
    settingsService
      .get()
      .then((res) => setSettings(res?.data || {}))
      .finally(() => setLoading(false));
  }, []);

  const onChange = (e) =>
    setSettings((s) => ({ ...s, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !validateForm(e.currentTarget, [
        {
          name: "ccs_calculator_url",
          value: settings.ccs_calculator_url,
          label: "CCS Calculator URL",
          type: "url",
        },
        {
          name: "reference_prefix",
          value: settings.reference_prefix,
          label: "Form reference prefix",
          required: true,
        },
        {
          name: "holiday_allowance",
          value: settings.holiday_allowance,
          label: "Allowable absence days",
          required: true,
          custom: (v) =>
            v && (!Number.isInteger(Number(v)) || Number(v) < 0)
              ? "Allowable absence days must be a whole number (0 or more)"
              : null,
        },
      ])
    )
      return;
    setSaving(true);
    try {
      const payload = {
        ccs_calculator_url: settings.ccs_calculator_url || "",
        reference_prefix: settings.reference_prefix || "SFP",
        handbook_pdf_label: settings.handbook_pdf_label || "",
        holiday_allowance: settings.holiday_allowance || "42",
      };
      if (file) payload.handbook_pdf = file;
      const res = await settingsService.save(payload);
      if (res?.success) {
        setSettings(res.data || settings);
        setFile(null);
        showToast("success", "Settings saved");
      } else showToast("error", res?.msg || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const pdfUrl = settings.handbook_pdf
    ? API_CONFIG.ASSET_BASE_URL + "uploads/" + settings.handbook_pdf.replace(/^uploads\//, "")
    : null;

  if (loading) return <div className="card mb-4"><div className="card-body">Loading…</div></div>;

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="card mb-4">
        <div className="card-header"><strong>Handbook PDF (universal)</strong></div>
        <div className="card-body">
          <p className="text-muted small">
            The group-wide handbook PDF downloaded by all families (v1). Centre-specific
            variations are a v2 consideration.
          </p>
          {pdfUrl && (
            <p>
              Current file:{" "}
              <a href={pdfUrl} target="_blank" rel="noreferrer">
                <i className="cil-cloud-download me-1" />
                {settings.handbook_pdf_label || "Download PDF"}
              </a>
            </p>
          )}
          <div className="mb-3">
            <label className="form-label">Replace handbook PDF</label>
            <input
              type="file"
              accept="application/pdf"
              className="form-control"
              onChange={(e) => setFile(e.target.files[0] || null)}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">PDF label</label>
            <input
              name="handbook_pdf_label"
              className="form-control"
              value={settings.handbook_pdf_label || ""}
              onChange={onChange}
              placeholder="PDF · 1.8 MB"
            />
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header"><strong>External links & references</strong></div>
        <div className="card-body">
          <div className="mb-3">
            <label className="form-label">CCS Calculator URL</label>
            <input
              name="ccs_calculator_url"
              className="form-control"
              value={settings.ccs_calculator_url || ""}
              onChange={onChange}
              placeholder="https://strideearlylearning.wa.edu.au/childcare-subsidy-calculator/"
            />
            <div className="form-text">
              Opens in a new tab from the portal — no rebuild needed.
            </div>
          </div>
          <div className="mb-3" style={{ maxWidth: 240 }}>
            <label className="form-label">Form reference prefix</label>
            <input
              name="reference_prefix"
              className="form-control"
              value={settings.reference_prefix || ""}
              onChange={onChange}
              placeholder="SFP"
              required
            />
            <div className="form-text">
              Used in submission references, e.g. <code>SFP-2026-000123</code>.
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header"><strong>Holiday / absences</strong></div>
        <div className="card-body">
          <div className="mb-3" style={{ maxWidth: 240 }}>
            <label className="form-label">
              Allowable absence days per child / year <span className="text-danger">*</span>
            </label>
            <input
              name="holiday_allowance"
              type="number"
              min="0"
              className="form-control"
              value={settings.holiday_allowance ?? ""}
              onChange={onChange}
              placeholder="42"
              required
            />
            <div className="form-text">
              Shown on the family Holiday Notification form as the absence-days balance
              (e.g. CCS covers up to 42 days per child each year).
            </div>
          </div>
        </div>
      </div>

      <button className="btn btn-primary" type="submit" disabled={saving}>
        {saving ? "Saving…" : "Save Settings"}
      </button>
    </form>
  );
}
